import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, Lock, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/mission", label: "Our Mission" },
  { to: "/eligibility", label: "Eligibility & Lineage" },
];

const bureauLinks = [
  { to: "/institution", label: "The Institution" },
  { to: "/membership", label: "Membership" },
  { to: "/tribal-portal", label: "Tribal Records Repository" },
];

const Monogram = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
    <img
      src={LOGO_URL}
      alt="7 Generations Indigenous Trust"
      className="h-16 w-auto object-contain"
    />
  </Link>
);

const linkCls = ({ isActive }) =>
  `font-sans text-[12px] tracking-[0.12em] uppercase transition-colors duration-300 ${
    isActive ? "text-gold" : "text-[#A19D98] hover:text-[#F5F0EB]"
  }`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-gold/15" : "bg-black/40 backdrop-blur-md border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Monogram />

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={linkCls}
            >
              {l.label}
            </NavLink>
          ))}

          {/* The Bureau dropdown */}
          <div className="relative group">
            <button
              data-testid="nav-bureau-trigger"
              className="flex items-center gap-1.5 font-sans text-[12px] tracking-[0.12em] uppercase text-[#A19D98] group-hover:text-gold transition-colors duration-300"
            >
              The Bureau <ChevronDown size={13} className="group-hover:rotate-180 transition-transform duration-300" />
            </button>
            <div className="absolute right-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
              <div className="w-60 bg-black/95 backdrop-blur-xl border border-gold/20 p-2">
                {bureauLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block px-4 py-3 font-sans text-[12px] tracking-[0.1em] uppercase transition-colors duration-200 ${
                        isActive ? "text-gold bg-gold/5" : "text-[#A19D98] hover:text-gold hover:bg-gold/5"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/contact" data-testid="nav-link-contact" className={linkCls}>Contact / Inquire</NavLink>

          {user?.is_admin && (
            <NavLink to="/admin" data-testid="nav-admin-link" className={({ isActive }) => `font-mono text-[11px] tracking-[0.16em] uppercase transition-colors ${isActive ? "text-gold" : "text-gold/80 hover:text-gold"}`}>
              Admin
            </NavLink>
          )}

          <button
            data-testid="nav-vault-btn"
            onClick={() => navigate(user ? "/vault" : "/login")}
            className="flex items-center gap-2 px-5 py-2.5 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-colors duration-300 font-mono text-[11px] tracking-[0.2em] uppercase"
          >
            <Lock size={13} />
            {user ? "The Vault" : "Vault Login"}
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-gold"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-gold/15 px-6 py-8 flex flex-col gap-5 max-h-[80vh] overflow-y-auto">
          {[...links, ...bureauLinks, { to: "/contact", label: "Contact / Inquire" }].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `font-sans text-sm tracking-[0.12em] uppercase ${isActive ? "text-gold" : "text-[#A19D98]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            data-testid="mobile-vault-btn"
            onClick={() => {
              setOpen(false);
              navigate(user ? "/vault" : "/login");
            }}
            className="flex items-center gap-2 px-5 py-3 border border-gold/50 text-gold font-mono text-[11px] tracking-[0.2em] uppercase w-fit"
          >
            <Lock size={13} /> {user ? "The Vault" : "Vault Login"}
          </button>
        </div>
      )}
    </header>
  );
}
